/* eslint-disable @typescript-eslint/no-explicit-any */
import type { UseMutationOptions } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { terreiroSignupRepository } from "../repositories/terreiroSignupRepository";
import type {
  QuickTerreiroSocialSignupPayload,
  QuickTerreiroSocialSignupResponse,
} from "../types/terreiroSignup.types";
import {
  normalizeTerreiroSignupError,
  type TerreiroSignupError,
} from "./useQuickTerreiroSignupMutation";
import { homeQueryKeys } from "./useHomeQueries";
import { sessionQueryKey } from "./useSessionQuery";

const toNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

const normalizeAvatarUrl = (
  incoming: Record<string, unknown>,
  fallback?: Record<string, unknown>,
): string | undefined => {
  return (
    toNonEmptyString(incoming.avatarUrl) ??
    toNonEmptyString(incoming.imageUrl) ??
    toNonEmptyString(incoming.picture) ??
    (fallback ? toNonEmptyString(fallback.avatarUrl) : undefined)
  );
};

export const useQuickTerreiroSocialSignupMutation = (
  options?: UseMutationOptions<
    QuickTerreiroSocialSignupResponse,
    TerreiroSignupError,
    QuickTerreiroSocialSignupPayload
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    QuickTerreiroSocialSignupResponse,
    TerreiroSignupError,
    QuickTerreiroSocialSignupPayload
  >({
    mutationFn: (data: QuickTerreiroSocialSignupPayload) =>
      terreiroSignupRepository.quickSignupSocial(data).catch((err) => {
        throw normalizeTerreiroSignupError(err);
      }),
    ...options,
    onSuccess: async (data, variables, context) => {
      try {
        if (data?.user) {
          const profile = data.user.profile;
          let normalizedProfile: Record<string, unknown> | undefined;
          const terreiro = data.terreiro;
          const terreiroId = terreiro?.id;
          const medium = data.medium;
          const mediumId = medium?.id;

          // ──────────────────────────────────────────────────────────
          // 1. Update localStorage FIRST so that useSessionQuery and
          //    useCachedTerreiro pick up the new terreiroId immediately.
          //    Social signups have no JWT token (authenticated via OAuth cookie).
          // ──────────────────────────────────────────────────────────

          if (profile) {
            const previousAuthProfile = queryClient.getQueryData<
              Record<string, unknown>
            >(["auth", "currentUser"]);
            const avatarUrl = normalizeAvatarUrl(
              profile as Record<string, unknown>,
              previousAuthProfile,
            );
            normalizedProfile = {
              ...(profile as Record<string, unknown>),
              ...(avatarUrl ? { avatarUrl } : {}),
            };

            try {
              localStorage.setItem(
                "ilovemacumba:auth:currentUser",
                JSON.stringify(normalizedProfile),
              );
            } catch {
              // ignore localStorage errors
            }
          }

          if (terreiroId && terreiro) {
            try {
              localStorage.setItem(
                "ilovemacumba:terreiro:current",
                JSON.stringify(terreiro),
              );
            } catch {
              // ignore localStorage errors
            }
          }

          if (mediumId && medium) {
            try {
              localStorage.setItem(
                "ilovemacumba:medium:current",
                JSON.stringify(medium),
              );
            } catch {
              // ignore localStorage errors
            }
          }

          // ──────────────────────────────────────────────────────────
          // 2. Pre-populate React Query caches to avoid loading flash.
          //    useSessionQuery uses [...sessionQueryKey, terreiroId ?? "personal"].
          //    Set on both "personal" (current key) and new terreiroId so the
          //    key transition is seamless.
          // ──────────────────────────────────────────────────────────

          queryClient.setQueryData([...sessionQueryKey, "personal"], data.user);
          if (terreiroId) {
            queryClient.setQueryData(
              [...sessionQueryKey, terreiroId],
              data.user,
            );
          }

          if (normalizedProfile) {
            queryClient.setQueryData(
              ["auth", "currentUser"],
              normalizedProfile,
            );
            queryClient.setQueryData(homeQueryKeys.homeProfile, (prev: any) => ({
              ...(prev && typeof prev === "object" ? prev : {}),
              profile: {
                ...((prev &&
                typeof prev === "object" &&
                prev.profile &&
                typeof prev.profile === "object"
                  ? prev.profile
                  : {}) as Record<string, unknown>),
                ...normalizedProfile,
              },
            }));
          }
          if (terreiroId && terreiro) {
            queryClient.setQueryData(["terreiro", terreiroId], terreiro);
            queryClient.setQueryData(["terreiro", "current"], terreiro);
          }
          if (mediumId && medium) {
            queryClient.setQueryData(["medium", mediumId], medium);
            queryClient.setQueryData(["medium", "current"], medium);
          }
        }

        // Background refetch to confirm session via /v1/auth/me.
        queryClient.invalidateQueries({ queryKey: sessionQueryKey });
        queryClient.invalidateQueries({ queryKey: ["profile"] });
        queryClient.invalidateQueries({ queryKey: ["medium", "me", "status"] });
        queryClient.invalidateQueries({ queryKey: ["medium", "me", "data"] });
      } catch {
        // swallow cache/localStorage errors
      }

      options?.onSuccess?.(data, variables, context as any);
    },
  });
};

export default useQuickTerreiroSocialSignupMutation;
